import { readActiveHomeRole, userHasModeratorRole } from "./homeRole";

/**
 * Who may edit session plan on Planning (durations, reorder): staff roles, not pure players.
 * Supports dual-role users (e.g. primary Player + Coach) via all_roles / hasCoachRole / permissions.
 */

/** Staff who may plan / conduct sessions: SA, Admin, Coach, Expert, Monitor, Moderator. */
const STAFF_ROLE_IDS = new Set(["r1", "r2", "r3", "r5", "r6", "r9"]);

/** Map DB/API role entries to canonical "rN" strings (handles 3, "r3", { role_id: "r5" }). */
function normalizeRoleIds(allRoles) {
  if (!Array.isArray(allRoles)) return [];
  return allRoles.map((r) => {
    let v = r && typeof r === "object" && r.role_id != null ? r.role_id : r;
    if (v == null) return "";
    const s = String(v).trim().toLowerCase();
    if (/^\d+$/.test(s)) return `r${s}`;
    if (s.startsWith("r") && /^r\d+$/.test(s)) return s;
    return s;
  });
}

/**
 * @param {object|null|undefined} user - from localStorage user
 * @param {Array} permissions - from PermissionContext (merged roles)
 * @returns {boolean}
 */
export function userCanEditPlanningSession(user, permissions) {
  if (!user) return false;
  if (user.isSuperAdmin) return true;

  const typeId = Number(user.type_user_id);
  if ([1, 2, 3].includes(typeId)) return true;

  // Backend may send boolean or string "true" after JSON round-trips
  if (
    user.hasCoachRole ||
    user.hasAdminRole ||
    user.hasModeratorRole ||
    user.hasMonitorRole
  ) {
    return true;
  }

  const roleIds = normalizeRoleIds(user.all_roles);
  if (roleIds.some((id) => STAFF_ROLE_IDS.has(id))) return true;

  const names = Array.isArray(user.all_role_names)
    ? user.all_role_names.map((n) => String(n).toLowerCase())
    : [];
  if (
    names.some(
      (n) =>
        n.includes("coach") ||
        n.includes("admin") ||
        n.includes("expert") ||
        n.includes("super") ||
        n.includes("moderator") ||
        n === "monitor" ||
        n.includes("entraîneur") ||
        n.includes("entraineur")
    )
  ) {
    return true;
  }

  if (Array.isArray(permissions) && permissions.length > 0) {
    const staffPerm = permissions.some((p) => {
      const ev = String(p?.enum_value || "");
      return (
        ev === "VIEW_GROUP" ||
        ev === "ADD_EXERCISE" ||
        ev === "EXERCISE_SWAP" ||
        ev === "SELECT_CB_EXERCISE" ||
        ev.includes("SESSION_") ||
        ev.includes("GROUP_") ||
        ev.includes("CREATE_GROUP") ||
        ev.includes("TRAINING") ||
        ev.includes("COACH") ||
        ev === "MANAGE_EXERCISES" ||
        ev.includes("EXERCISE_ADD") ||
        ev.includes("EXERCISE_STUDIO")
      );
    });
    if (staffPerm) return true;
  }

  return false;
}

/**
 * Planning edit rights respecting Home "View as player" (dual-role users).
 * @param {object|null|undefined} user
 * @param {Array} permissions
 * @returns {boolean}
 */
export function userCanEditPlanningInCurrentView(user, permissions) {
  if (readActiveHomeRole(user) === "player") return false;
  return userCanEditPlanningSession(user, permissions);
}

export function readUserFromStorage() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** True when Planning / Training should be view-only (no coach edits). */
export function isSessionReadOnlyView(user, permissions) {
  return !userCanEditPlanningInCurrentView(user, permissions);
}

/**
 * Create, duplicate, or delete planned sessions on Home (coach/staff).
 * Uses SESSION_ADD when assigned, otherwise the same staff rules as planning edit.
 *
 * @param {object|null|undefined} user
 * @param {Array} permissions
 * @param {(enumValue: string) => boolean} [hasPermission]
 * @returns {boolean}
 */
export function userCanManageTrainingSessions(user, permissions, hasPermission) {
  const homeRole = readActiveHomeRole(user);
  // Players (or dual-role users viewing as player) never create sessions
  if (homeRole === "player") return false;
  // type_user_id 4 accounts can still be coaches/monitors/admins via Home role
  const staffHomeRoles = new Set([
    "coach",
    "admin",
    "monitor",
    "moderator",
    "committee",
  ]);
  if (
    Number(user?.type_user_id) === 4 &&
    !staffHomeRoles.has(homeRole)
  ) {
    return false;
  }
  if (typeof hasPermission === "function" && hasPermission("SESSION_ADD")) {
    return true;
  }
  return userCanEditPlanningInCurrentView(user, permissions);
}

/**
 * Moderator / club Admin / Super Admin may manage past dates and finalized sessions
 * (create/delete/edit presence/blocks). Coaches stay limited to non-finalized plans.
 * Mirrors Backend pastTrainingSessionAccess + brandAccess elevation.
 *
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export function userCanManagePastTrainingSessions(user) {
  if (!user) return false;

  const elevated =
    user.isSuperAdmin === true ||
    Number(user.type_user_id) === 1 ||
    Number(user.type_user_id) === 2 ||
    user.hasModeratorRole === true ||
    userHasModeratorRole(user) ||
    user.hasAdminRole === true ||
    normalizeRoleIds(user.all_roles).some(
      (id) => id === "r1" || id === "r2" || id === "r9"
    ) ||
    (Array.isArray(user.all_role_names) &&
      user.all_role_names.some((n) => {
        const low = String(n).toLowerCase();
        return (
          low === "moderator" ||
          low === "admin" ||
          low.includes("super admin")
        );
      }));

  if (!elevated) return false;
  // Dual-role users viewing Home as player stay view-only.
  if (readActiveHomeRole(user) === "player") return false;
  return true;
}

/**
 * Who may edit table seating on a finalized exercise from session results.
 * Past-session managers (moderator/admin) plus coaches/monitors in a staff
 * Home role — same people who already correct presence after finalize.
 *
 * @param {object|null|undefined} user
 * @param {Array} [permissions]
 * @returns {boolean}
 */
export function userCanEditFinalizedExerciseSeating(user, permissions) {
  if (!user) return false;
  if (readActiveHomeRole(user) === "player") return false;
  if (userCanManagePastTrainingSessions(user)) return true;
  return userCanEditPlanningSession(user, permissions);
}

/**
 * Player id for the logged-in user (roster-free fallback on Planning).
 * @param {object|null|undefined} user
 * @returns {string|null}
 */
export function resolveViewerPlayerIdFromUser(user) {
  if (!user) return null;
  if (user.player_id != null && String(user.player_id).trim()) {
    return String(user.player_id);
  }
  return null;
}
